import React, { useState, useEffect } from "react";
// import PropTypes from "prop-types";
import PrimeCalendar from "./Calendar";
import { Dropdown as PrimeDropdown } from "primereact/dropdown";
import { MultiSelect as PrimeMultiSelect } from "primereact/multiselect";
import { PrimaryButton } from "./Buttons";

export const Label = ({
  className = "w3-block",
  children,
  htmlFor,
  noMargin = false,
  style,
}) => (
  <label
    className={`bold ${noMargin ? "" : "margin-sm-btm"} ${className}`}
    htmlFor={htmlFor}
    style={style}
  >
    {children}
  </label>
);

const InputTextModule = ({
  forwardedRef,
  className,
  readonly,
  readOnly,
  small,
  ...props
}) => {
  if (readonly || readOnly) {
    const { value, ...others } = props;
    return (
      <div {...others} className={`${className} w3-round- pad-`}>
        {value}
        {value === "" && <>&nbsp;</>}
      </div>
    );
  }
  const pad = `pad${small ? "-lr pad-sm-tb" : ""}`;
  return (
    <input
      ref={forwardedRef}
      {...props}
      type="text"
      className={`${className} w3-border w3-block w3-round ${pad}`}
    />
  );
};
export const InputText = React.forwardRef((props, ref) => {
  return <InputTextModule {...props} forwardedRef={ref} />;
});

export const InputTextHolder = ({ onClear, title, children }) => (
  <div className="inputtext-clear-icon-holder pos-rel expand">
    {children}
    <span
      className="inputtext-clear-icon clickable"
      onClick={() => onClear && onClear()}
      title={title || "löschen"}
    >
      <i className="p-dropdown-clear-icon pi pi-times" />
    </span>
  </div>
);

// Password
const PasswordModule = ({ forwardedRef, className, small, ...props }) => {
  const pad = `pad${small ? "-lr pad-sm-tb" : ""}`;
  return (
    <input
      ref={forwardedRef}
      {...props}
      type="password"
      className={`${className} w3-border w3-block w3-round ${pad}`}
    />
  );
};
export const Password = React.forwardRef((props, ref) => {
  return <PasswordModule {...props} forwardedRef={ref} />;
});

// Calendar
export const Calendar = PrimeCalendar;

// Dropdown
const DropdownModule = ({
  forwardedRef,
  className,
  options,
  readOnly,
  ...props
}) => {
  if (readOnly) {
    const { showClear, ...others } = props;
    return (
      <InputText
        className={`${className} pad w3-round`}
        readonly={readOnly}
        {...others}
      />
    );
  }
  return (
    <PrimeDropdown
      ref={forwardedRef}
      className={`${className} w3-border w3-block w3-round`}
      appendTo={document.body}
      options={options}
      filter={options.length > 5}
      {...props}
    />
  );
};
export const Dropdown = React.forwardRef((props, ref) => {
  return <DropdownModule {...props} forwardedRef={ref} />;
});

// CalendarRange
export const CalendarRange = ({ value, onChange, isHeuteDisabled, ...props }) => {
  // Local state to track incomplete selections (for range building)
  const [pendingSelection, setPendingSelection] = useState(null);

  // Derive what to pass to PrimeCalendar based on parent value or local pending
  const calendarValue = (value && value.length > 0) ? value : pendingSelection;

  // check for two values
  const isComplete = (list) => {
    return !list || !Array.isArray(list) || list.length < 2 ? false : true;
  };

  const handleChange = (event) => {
    if (isComplete(event.value)) {
      setPendingSelection(null);
      if (onChange) {
        onChange(event);
      }
    } else {
      // Store incomplete selection locally so calendar shows partial range
      setPendingSelection(event.value);
    }
  };

  const handleClear = () => {
    setPendingSelection(null);
    // Reset constraints by notifying parent
    if (props.onClear) {
      props.onClear();
    }
    // Re-focus the input after clearing to keep dialog open
    setTimeout(() => {
      const input = document.querySelector(".p-datepicker-panel:not(.p-disabled) input");
      if (input) input.focus();
    }, 0);
  };

  const handleToday = () => {
    // Set end date to today
    const newDates = value && value[0] ? [value[0], new Date()] : [new Date(), new Date()];
    if (onChange) {
      onChange({ value: newDates });
    }
  };

  // Custom button bar template - conditionally show Heute button
  const buttonBarTemplate = () => (
    <div className={`p-datepicker-buttonbar p-d-flex p-jc-between p-p-2`}>
      <PrimaryButton
        size="small"
        onClick={handleToday}
        disabled={isHeuteDisabled}
      >
        {`Heute`}
      </PrimaryButton>
      <PrimaryButton
        size="small"
        onClick={handleClear}
      >
        {`Leeren`}
      </PrimaryButton>
    </div>
  );
  
  return (
    <PrimeCalendar
      className="w3-block w3-border w3-round"
      value={calendarValue}
      onChange={handleChange}
      selectionMode="range"
      readOnlyInput
      footerTemplate={buttonBarTemplate}
      {...props}
    />
  );
};

// MultiSelect
export const MultiSelectHolder = ({ onClear, title, children }) => (
  <div className="pos-rel expand">
    {children}
    <span
      className="multiselect-clear-icon clickable"
      onClick={() => onClear && onClear()}
      title={title || "löschen"}
    >
      <i className="p-dropdown-clear-icon pi pi-times" />
    </span>
  </div>
);
const MultiSelectModule = ({
  forwardedRef,
  className,
  onChange,
  clearTitle,
  readOnly,
  ...props
}) => {
  if (readOnly) {
    return (
      <InputText
        className={`${className} pad w3-round`}
        readonly={readOnly}
        value={props.value}
      />
    );
  }
  return (
    <MultiSelectHolder
      onClear={() => onChange && onChange({ value: [] })}
      title={clearTitle}
    >
      <PrimeMultiSelect
        ref={forwardedRef}
        appendTo={document.body}
        {...props}
        onChange={onChange}
        className={`${className} w3-border expand w3-round`}
      />
    </MultiSelectHolder>
  );
};
export const MultiSelect = React.forwardRef((props, ref) => {
  return <MultiSelectModule {...props} forwardedRef={ref} />;
});
